"use client";

import { FetchButton } from "@/fetch-ui/FetchButton";
import { Copy, ExternalLink, Check } from "lucide-react";
import { useState, useEffect } from "react";

export default function IntegrationsPage() {
  // States
  const [copied, setCopied] = useState(false);
  const [selectedHeight, setSelectedHeight] = useState("800px");
  const [baseUrl, setBaseUrl] = useState("https://getnowadays.com");

  // Effects
  useEffect(() => {
    setBaseUrl(window.location.origin);
  }, []);

  // Constants
  const embedUrl = `${baseUrl}/embed/create-event`;

  const iframeCode = `<iframe
  src="${embedUrl}"
  width="100%"
  height="${selectedHeight}"
  frameborder="0"
  allow="geolocation"
  style="border: none; border-radius: 8px;"
  title="Nowadays Event Request Form"
></iframe>`;

  // Handlers
  const handleCopy = () => {
    navigator.clipboard.writeText(iframeCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Render
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-indigo-50/30">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-6 max-w-7xl">
          <h1 className="text-3xl font-bold text-gray-900">Event Form Integration</h1>
          <p className="text-gray-600 mt-2">Embed the Nowadays event request form on your website</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-7xl">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left Column - Code & Instructions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Start */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Start</h2>
              <p className="text-gray-600 mb-4">
                Copy the iframe code below and paste it into your website's HTML where you want the form to appear.
              </p>

              {/* Height Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Form Height</label>
                <select
                  value={selectedHeight}
                  onChange={(e) => setSelectedHeight(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="600px">600px (Compact)</option>
                  <option value="800px">800px (Standard)</option>
                  <option value="1000px">1000px (Tall)</option>
                  <option value="100vh">100vh (Full Height)</option>
                </select>
              </div>

              {/* Code Block */}
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{iframeCode}</code>
                </pre>
                <button
                  onClick={handleCopy}
                  className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors"
                  tabIndex={0}
                  aria-label="Copy code"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCopy();
                    }
                  }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {copied && <p className="text-sm text-green-600 mt-2">✓ Copied to clipboard!</p>}
            </div>

            {/* Features */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Features</h2>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span className="text-gray-700">Fully responsive design that adapts to your website</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span className="text-gray-700">Auto-save functionality preserves user progress</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span className="text-gray-700">Multi-step form with progress tracking</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span className="text-gray-700">Built-in validation and error handling</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span className="text-gray-700">Venue search powered by Google Places API</span>
                </li>
              </ul>
            </div>

            {/* Documentation Link */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                Documentation
                <ExternalLink className="w-5 h-5 ml-2 text-indigo-600" />
              </h2>
              <p className="text-gray-700 mb-4">
                For advanced integration options, customization, and API documentation, visit our developer docs.
              </p>
              <a href="https://docs.getnowadays.com/" target="_blank" rel="noopener noreferrer" tabIndex={0}>
                <FetchButton variant="primary" className="w-full sm:w-auto">
                  View Documentation
                </FetchButton>
              </a>
            </div>

            {/* Additional Options */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Integration Options</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">1. Iframe Embed (Recommended)</h3>
                  <p className="text-sm text-gray-600">Simple copy-paste integration. Best for quick implementation.</p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">2. Direct Link</h3>
                  <p className="text-sm text-gray-600 mb-2">Link directly to the form:</p>
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded">{embedUrl}</code>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-2">3. API Integration</h3>
                  <p className="text-sm text-gray-600">
                    Build a custom integration using our API. See documentation for details.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Live Preview */}
          <div className="lg:col-span-3 lg:sticky lg:top-8 lg:self-start">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Live Preview</h2>
              <p className="text-gray-600 mb-4 text-sm">This is how the form will appear on your website:</p>
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                <iframe
                  src={embedUrl}
                  width="100%"
                  height={selectedHeight}
                  style={{ border: "none" }}
                  allow="geolocation"
                  title="Nowadays Event Request Form Preview"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">Note: The form is fully functional in this preview</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
